/**
 * Pure helpers for the "Autofill picks" action (reveal + adopt auto-seeded monkey picks).
 *
 * A match is "autofillable" when it is still editable (kickoff in the future), is currently in
 * monkey state (server flagged isMonkeyPick = true), and the user has not already started editing
 * it locally — i.e. exactly the matches the Picks tab masks as `-`. See MatchCard's
 * `isMaskedMonkeyPick` for the rendering counterpart.
 */

import type { WorldCupMatch } from '../types/worldcup.types';
import type { MatchPredictionInput } from '../hooks/useWorldCupPicksData';
import type { UserMatchesOverlayPayload } from './worldCupMatchesApi.helper';

/**
 * Count the matches an autofill would reveal: still-editable, monkey-state, and not yet edited
 * locally. Mirrors the mask condition so the button's number matches what the user sees as `-`.
 */
export function countAutofillableMonkeyPicks(
  matches: readonly WorldCupMatch[],
  predictions: Record<string, MatchPredictionInput>,
): number {
  let count = 0;
  for (const match of matches) {
    if (
      match.prediction?.isMonkeyPick === true
      && match.isPickEditable !== false
      && predictions[match.id] == null
    ) {
      count += 1;
    }
  }
  return count;
}

/**
 * Patch the per-user overlay cache after a reveal: flip isMonkeyPick → false for the returned
 * matchIds, leaving every other pick — and all predicted scores / pointsEarned — untouched.
 */
export function applyRevealedMonkeyPicks(
  overlay: UserMatchesOverlayPayload | undefined,
  revealedMatchIds: readonly string[],
): UserMatchesOverlayPayload | undefined {
  if (!overlay) return overlay;
  if (revealedMatchIds.length === 0) return overlay;

  const ids = new Set(revealedMatchIds);
  const nextPicks: UserMatchesOverlayPayload['picks'] = { ...overlay.picks };
  let changed = false;

  for (const matchId of ids) {
    const pick = nextPicks[matchId];
    if (pick && pick.isMonkeyPick !== false) {
      nextPicks[matchId] = { ...pick, isMonkeyPick: false };
      changed = true;
    }
  }

  if (!changed) return overlay;
  return { ...overlay, picks: nextPicks };
}
